const BREAK_STATUSES = new Set(['summer break'])
const TEACHER_LEFT_STATUSES = new Set(['leaving', 'left school'])
const CONTINUING_TEACHER_STATUSES = new Set(['active', 'summer-break'])

const SCHOOL_NAMES = {
    mma: 'Mountainside',
    lsma: 'Lakeside',
    tma: 'Treeside',
    mor: 'Morningside',
    psma: 'Parkside',
    den: 'Denton',
    tem: 'Tempe',
    tul: 'Tulsa',
}

const normalize = (value) => String(value || '').trim().toLowerCase()

const isBreakStatus = (value) => BREAK_STATUSES.has(normalize(value))

const looksLikeDatabaseId = (value) => /^[a-f\d]{24}$/i.test(String(value || '').trim())

const buildInstrumentDirectory = (teachers, families, cards) => {
    const directory = new Map()
    const addInstruments = (instrumentList) => {
        ;(Array.isArray(instrumentList) ? instrumentList : []).forEach((instrument) => {
            if (!instrument || typeof instrument !== 'object') return

            const id = String(instrument.id || '').trim()
            const name = String(instrument.name || '').trim()
            if (id && name) directory.set(id, name)
        })
    }

    ;(Array.isArray(teachers) ? teachers : []).forEach((teacher) => {
        addInstruments(teacher?.InstrumentList)
    })
    ;(Array.isArray(families) ? families : []).forEach((family) => {
        ;(Array.isArray(family?.students) ? family.students : []).forEach((student) => {
            addInstruments(student?.instrumentList)
        })
    })
    ;(Array.isArray(cards) ? cards : []).forEach((card) => {
        ;(Array.isArray(card?.studentList) ? card.studentList : []).forEach((student) => {
            addInstruments(student?.instrumentList)
        })
    })

    return directory
}

const getInstrumentNames = (student, instrumentDirectory) => {
    const instrumentNames = (Array.isArray(student?.instrumentList) ? student.instrumentList : [])
        .map((instrument) => {
            if (instrument && typeof instrument === 'object') {
                const name = String(instrument.name || '').trim()
                const id = String(instrument.id || '').trim()
                return name || instrumentDirectory.get(id) || ''
            }

            const value = String(instrument || '').trim()
            return instrumentDirectory.get(value) || (looksLikeDatabaseId(value) ? '' : value)
        })
        .filter(Boolean)

    if (instrumentNames.length === 0) {
        const legacyInstrument = String(student?.instrument || '').trim()
        if (legacyInstrument && !looksLikeDatabaseId(legacyInstrument)) {
            instrumentNames.push(legacyInstrument)
        }
    }

    const uniqueNames = new Map()
    instrumentNames.forEach((name) => {
        const normalizedName = normalize(name)
        if (normalizedName && !uniqueNames.has(normalizedName)) {
            uniqueNames.set(normalizedName, name)
        }
    })

    return Array.from(uniqueNames.values())
}

const getStudentKey = (student, family, index) => {
    const studentId = String(student?.studentId || '').trim()
    if (studentId) return studentId

    return [
        String(family?.familyId || family?.todoId || '').trim(),
        normalize(student?.studentName),
        index,
    ].join(':')
}

export const buildSummerBreakSummary = ({
    families = [],
    cards = [],
    teachers = [],
    schools = [],
} = {}) => {
    const instrumentDirectory = buildInstrumentDirectory(teachers, families, cards)
    const teachersById = new Map(
        teachers
            .map((teacher) => [String(teacher?.TeacherID || '').trim(), teacher])
            .filter(([teacherId]) => teacherId)
    )
    const summerBreakTeacherIds = new Set(
        teachers
            .filter((teacher) => normalize(teacher?.Status) === 'summer-break')
            .map((teacher) => String(teacher?.TeacherID || '').trim())
            .filter(Boolean)
    )
    const studentsByKey = new Map()

    const getTeacherPlan = (student) => {
        const teacherId = String(student?.teachersId || '').trim()
        const teacher = teachersById.get(teacherId)
        const teacherName = String(teacher?.Name || '').trim()
        const teacherStatus = normalize(teacher?.Status)

        if (
            normalize(student?.studentStatus) === 'needs new teacher'
            || TEACHER_LEFT_STATUSES.has(teacherStatus)
        ) {
            return {
                teacherId,
                teacherName,
                teacherStatus,
                teacherPlan: 'needs-new-teacher',
                teacherPlanLabel: teacherName
                    ? `${teacherName} ${teacherStatus === 'leaving' ? 'is leaving' : 'left'}`
                    : 'Needs a new teacher',
            }
        }

        if (teacher && CONTINUING_TEACHER_STATUSES.has(teacherStatus)) {
            return {
                teacherId,
                teacherName,
                teacherStatus,
                teacherPlan: 'continuing',
                teacherPlanLabel: teacherName
                    ? `Continuing with ${teacherName}`
                    : 'Continuing with current teacher',
            }
        }

        return {
            teacherId,
            teacherName,
            teacherStatus,
            teacherPlan: 'needs-review',
            teacherPlanLabel: 'Teacher assignment needs review',
        }
    }

    const addStudent = (student, family, index, schoolValue) => {
        if (!student) return

        const schoolId = normalize(schoolValue || family?.school || family?.whichSchool)
        if (!schoolId) return

        const studentKey = getStudentKey(student, family, index)
        const currentStudent = studentsByKey.get(studentKey)
        const instrumentNames = getInstrumentNames(student, instrumentDirectory)
        const teacherPlan = getTeacherPlan(student)

        if (currentStudent) {
            const mergedInstruments = new Map(
                [...currentStudent.instrumentNames, ...instrumentNames]
                    .map((name) => [normalize(name), name])
            )
            currentStudent.instrumentNames = Array.from(mergedInstruments.values())
            if (!currentStudent.familyAddress && (family?.address || family?.familyAddress)) {
                currentStudent.familyAddress = family.address || family.familyAddress
            }
            if (!currentStudent.availability && (student?.initialAvailability || family?.initialAvailability)) {
                currentStudent.availability = student.initialAvailability || family.initialAvailability
            }
            if (!currentStudent.lessonLength && student?.lessonLength) {
                currentStudent.lessonLength = student.lessonLength
            }
            if (currentStudent.instrumentList.length === 0 && Array.isArray(student?.instrumentList)) {
                currentStudent.instrumentList = student.instrumentList
            }
            const planPriority = {
                'needs-review': 1,
                continuing: 2,
                'needs-new-teacher': 3,
            }
            if (planPriority[teacherPlan.teacherPlan] > planPriority[currentStudent.teacherPlan]) {
                Object.assign(currentStudent, teacherPlan)
            }
            return
        }

        studentsByKey.set(studentKey, {
            studentId: String(student?.studentId || studentKey),
            studentName: String(student?.studentName || 'Unnamed student').trim(),
            familyId: String(family?.familyId || family?.todoId || '').trim(),
            familyName: String(family?.familyName || family?.todoName || '').trim(),
            familyAddress: family?.address || family?.familyAddress || null,
            availability: student?.initialAvailability || family?.initialAvailability || null,
            availabilityNotes: student?.initialAvailabilityNotes || family?.initialAvailabilityNotes || '',
            lessonLength: Number(student?.lessonLength) || 30,
            schoolId,
            instrumentNames,
            instrumentList: Array.isArray(student?.instrumentList) ? student.instrumentList : [],
            ...teacherPlan,
        })
    }

    families.forEach((family) => {
        const familyIsOnBreak = isBreakStatus(family?.familyStatus)
        const familyStudents = Array.isArray(family?.students) ? family.students : []

        familyStudents.forEach((student, index) => {
            if (familyIsOnBreak || isBreakStatus(student?.studentStatus)) {
                addStudent(student, family, index, family?.school)
            }
        })
    })

    cards
        .filter((card) => card?.whichList === 'leaving' && card?.summerBreak)
        .forEach((card) => {
            const cardStudents = Array.isArray(card?.studentList) ? card.studentList : []
            const affectedStudents = cardStudents.filter((student) => (
                isBreakStatus(student?.studentStatus)
                || summerBreakTeacherIds.has(String(student?.teachersId || '').trim())
            ))
            const studentsToAdd = affectedStudents.length > 0 ? affectedStudents : cardStudents

            studentsToAdd.forEach((student, index) => {
                addStudent(student, card, index, card?.whichSchool)
            })
        })

    const students = Array.from(studentsByKey.values())
    const schoolDetails = new Map()

    schools
        .filter((school) => normalize(school?.id) !== 'all')
        .forEach((school) => {
            const schoolId = normalize(school?.id)
            if (!schoolId) return
            schoolDetails.set(schoolId, {
                id: schoolId,
                name: school?.label || SCHOOL_NAMES[schoolId] || schoolId.toUpperCase(),
            })
        })

    students.forEach((student) => {
        if (!schoolDetails.has(student.schoolId)) {
            schoolDetails.set(student.schoolId, {
                id: student.schoolId,
                name: SCHOOL_NAMES[student.schoolId] || student.schoolId.toUpperCase(),
            })
        }
    })

    const schoolSummaries = Array.from(schoolDetails.values()).map((school) => {
        const schoolStudents = students
            .filter((student) => student.schoolId === school.id)
            .sort((a, b) => a.studentName.localeCompare(b.studentName))
        const instrumentsByName = new Map()

        schoolStudents.forEach((student) => {
            const names = student.instrumentNames.length > 0
                ? student.instrumentNames
                : ['Instrument not listed']

            names.forEach((instrumentName) => {
                const instrumentKey = normalize(instrumentName)
                if (!instrumentsByName.has(instrumentKey)) {
                    instrumentsByName.set(instrumentKey, {
                        name: instrumentName,
                        students: [],
                    })
                }
                instrumentsByName.get(instrumentKey).students.push(student)
            })
        })

        const instruments = Array.from(instrumentsByName.values())
            .map((instrument) => ({
                ...instrument,
                count: instrument.students.length,
            }))
            .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))

        return {
            ...school,
            totalStudents: schoolStudents.length,
            needsNewTeacher: schoolStudents.filter(
                (student) => student.teacherPlan === 'needs-new-teacher'
            ).length,
            continuingWithTeacher: schoolStudents.filter(
                (student) => student.teacherPlan === 'continuing'
            ).length,
            teacherNeedsReview: schoolStudents.filter(
                (student) => student.teacherPlan === 'needs-review'
            ).length,
            students: schoolStudents,
            instruments,
        }
    })

    return {
        totalStudents: students.length,
        totalInstrumentPlacements: schoolSummaries.reduce(
            (total, school) => total + school.instruments.reduce(
                (schoolTotal, instrument) => schoolTotal + instrument.count,
                0
            ),
            0
        ),
        schoolsWithStudents: schoolSummaries.filter((school) => school.totalStudents > 0).length,
        needsNewTeacher: students.filter(
            (student) => student.teacherPlan === 'needs-new-teacher'
        ).length,
        continuingWithTeacher: students.filter(
            (student) => student.teacherPlan === 'continuing'
        ).length,
        teacherNeedsReview: students.filter(
            (student) => student.teacherPlan === 'needs-review'
        ).length,
        teacherDirectory: teachers,
        schools: schoolSummaries,
    }
}
